import * as helios from "@hyperionbt/helios";
import {
  CardanoNetwork,
  KreateUTxO,
  Artwork,
  DatumArtwork,
} from "kreate-common/modules/business-types";
import {
  MetadataEnvOffChain,
  MetadataEnvOnChain,
} from "kreate-common/modules/metadata-env/types";
import { CLIENT_ENV } from "kreate-env/client";
import { httpPost$ModerateMintTx } from "kreate-server/commands/ModerateMintTx/fetcher";

import MintingPolicyCollection from "../../onchain/mpCollection.hl";
import ScriptArtwork from "../../onchain/scriptArtwork.hl";
import {
  addHeliosCollateral,
  getCardanoNetworkParams,
  toHeliosDatum,
  toHeliosMetadataV2,
  toHeliosUTxO,
} from "../../utils";

type Params = {
  network: CardanoNetwork;
  wallet: helios.Cip30Wallet;
  utxoProtocolParams: KreateUTxO;
  utxoArtwork: KreateUTxO;
  artwork: Artwork;
  mintNumber: number;
  txMessage: string;
  metadataEnvOffChain: MetadataEnvOffChain;
  fileIdToText: Record<string, string>;
};

export async function buildPartialTx$MintArtwork({
  network,
  wallet,
  utxoProtocolParams,
  utxoArtwork,
  artwork,
  mintNumber,
  txMessage,
  metadataEnvOffChain,
  fileIdToText,
}: Params) {
  if (utxoProtocolParams.datum.type !== "protocol-params") {
    throw new Error("wrong datum type for protocol params UTxO");
  }
  if (utxoArtwork.datum.type !== "artwork") {
    throw new Error("wrong datum type for artwork UTxO");
  }

  const walletHelper = new helios.WalletHelper(wallet);

  const address = await walletHelper.baseAddress;

  const protocolParamsHeliosUTxO = toHeliosUTxO(utxoProtocolParams);
  const protocolParamsDatum = utxoProtocolParams.datum.value;

  const artworkHeliosUTxO = toHeliosUTxO(utxoArtwork);
  const artworkDatum = utxoArtwork.datum.value;

  const ownerAddress = new helios.Address(artworkDatum.ownerAddress);
  const isOwner = address.pubKeyHash.eq(ownerAddress.pubKeyHash);

  const tx = new helios.Tx();
  await addHeliosCollateral(wallet, tx);

  tx.addRefInput(
    new helios.TxRefInput(
      protocolParamsHeliosUTxO.txId,
      protocolParamsHeliosUTxO.utxoIdx,
      protocolParamsHeliosUTxO.origOutput
    )
  );
  tx.addInput(artworkHeliosUTxO, new helios.ConstrData(1, []));

  let protocolFee = 0;
  let mintFee = 0;
  if (mintNumber > 0) {
    if (isOwner) {
      protocolFee = protocolParamsDatum.kreatorMintFee * mintNumber;
    } else {
      mintFee = artworkDatum.mintFee * mintNumber;
      protocolFee = (mintFee * CLIENT_ENV.KOLLECTOR_MINT_FEE_PERCENTAGE) / 100;
    }
  }

  protocolFee = Math.max(protocolFee, CLIENT_ENV.MIN_PROTOCOL_FEE);

  if (protocolFee > 0) {
    tx.addOutput(
      new helios.TxOutput(
        new helios.Address(protocolParamsDatum.treasuryAddress),
        new helios.Value(BigInt(protocolFee))
      )
    );
  }

  if (mintFee) {
    tx.addOutput(
      new helios.TxOutput(ownerAddress, new helios.Value(BigInt(mintFee)))
    );
  }

  // TODO: Improve coin selection here
  // Especially for burning
  const [utxos, remainingUtxos] = await walletHelper.pickUtxos(
    // Ad-hoc padding...
    new helios.Value(BigInt(protocolFee + mintFee + 2_000_000)),
    helios.CoinSelection.selectLargestFirst
  );
  tx.addInputs(utxos);

  const newArtworkDatum: DatumArtwork = {
    ...artworkDatum,
    numMinted: artworkDatum.numMinted + mintNumber,
  };

  tx.addOutput(
    new helios.TxOutput(
      artworkHeliosUTxO.origOutput.address,
      // We must "copy" a new value here.
      // Referencing will balance incorrectly if the new value is updated
      // by Helios to fit min lovelace from the changed datum size,
      // which will also slyly update the value in the input UTxO!
      helios.Value.fromCbor(artworkHeliosUTxO.origOutput.value.toCbor()),
      toHeliosDatum({
        type: "artwork",
        value: newArtworkDatum,
      })
    )
  );

  const scriptArtwork = new ScriptArtwork().compile(true);
  tx.attachScript(scriptArtwork);

  const mpCollection = new MintingPolicyCollection({
    // Assume that there is only one asset at the protocol params UTxO
    MASTER_MPH: protocolParamsHeliosUTxO.value.assets.mintingPolicies[0],
    ARTWORK_SCRIPT_ADDRESS: artworkHeliosUTxO.origOutput.address,
    OWNER_ADDRESS: ownerAddress,
    COLLECTION_ID: new helios.ByteArray(
      helios.hexToBytes(artworkDatum.collectionId)
    ),
  }).compile(true);

  tx.attachScript(mpCollection);

  const tokenName = helios.textToBytes(artworkDatum.tokenName);
  tx.mintTokens(
    mpCollection.mintingPolicyHash,
    [[tokenName, BigInt(mintNumber)]],
    new helios.ConstrData(1, [])
  );

  // Naive: include all UTxOs that have a burning token
  // even when `utxos` already have enough.
  if (mintNumber < 0) {
    const remainingUtxOsWithToken = remainingUtxos.filter((utxo) =>
      utxo.value.assets.has(mpCollection.mintingPolicyHash, tokenName)
    );
    if (remainingUtxOsWithToken.length > 0) {
      tx.addInputs(remainingUtxOsWithToken);
    }
  }

  const metadataEnvOnChain = MetadataEnvOnChain.parse({
    datumArtwork: newArtworkDatum,
  } satisfies MetadataEnvOnChain);

  tx.addMetadata(
    721,
    toHeliosMetadataV2({
      policyId: artwork.utxo?.otherAssets[0].mintingPolicyHash || "",
      tokenName: artwork.artworkId,
      artwork,
      metadataEnv: { ...metadataEnvOffChain, ...metadataEnvOnChain },
      fileIdToText,
    })
  );

  /** NOTE: @thanh-kreate
   * Since helios api does not automatically break a string that is less than 64
   * characters to array, this should be done manually to satisfy CIP-20 constraints
   * More details: https://cips.cardano.org/cips/cip20/
   * */
  tx.addMetadata(674, {
    map: [
      [
        "msg",
        helios.textToBytes(txMessage).length <= 64 ? [txMessage] : txMessage,
      ],
    ],
  });

  if (isOwner) {
    tx.addSigner(ownerAddress.pubKeyHash);
  } else {
    tx.addSigner(new helios.PubKeyHash(protocolParamsDatum.modPKH));
  }

  await tx.finalize(getCardanoNetworkParams(network), address, remainingUtxos);

  if (!isOwner) {
    const { signature } = await httpPost$ModerateMintTx("/api/v1/tx/mint", {
      tx: helios.bytesToHex(tx.toCbor()),
    });
    tx.addSignature(helios.Signature.fromCbor(helios.hexToBytes(signature)));
  }

  const assetIdParts = {
    policyId: mpCollection.mintingPolicyHash.bytes,
    tokenName,
  };

  const txFee = Number(tx.body.fee);

  return {
    tx,
    mpCollection,
    artworkDatum,
    assetIdParts,
    protocolFee,
    mintFee,
    txFee,
    isOwner,
  };
}
